import React, { useState, useEffect } from 'react';
import { HeartIcon, MessageIcon, LocationIcon, ArrowLeftIcon, ShareIcon } from './Icons';
import { ImageWithPlaceholder, AvatarWithFallback } from './Placeholders';
import { apiService, useAuth } from '../auth';

// Composant pour les détails d'une annonce
export const ListingDetailScreen = ({ listingId, onBack, onContact }) => {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isContacting, setIsContacting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const loadListing = async () => {
      if (!listingId) return;
      
      setLoading(true);
      try {
        const listingData = await apiService.getListingById(listingId);
        setListing(listingData);
        setIsLiked(listingData.is_favorited || false);
        
        // Incrémenter le nombre de vues
        await apiService.incrementListingViews(listingId);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'annonce:', error);
      } finally {
        setLoading(false);
      }
    };

    loadListing();
  }, [listingId]);

  const handleLike = async () => {
    if (isProcessing || !user) return;
    
    setIsProcessing(true);
    try {
      if (isLiked) {
        const result = await apiService.removeFromFavorites(listing.id);
        if (result.success) {
          setIsLiked(false);
          setListing(prev => ({
            ...prev,
            favorites_count: (prev.favorites_count || 0) - 1
          }));
        }
      } else {
        const result = await apiService.addToFavorites(listing.id);
        if (result.success) {
          setIsLiked(true);
          setListing(prev => ({
            ...prev,
            favorites_count: (prev.favorites_count || 0) + 1
          }));
        }
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des favoris:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContact = async () => {
    if (isContacting || !user) return;
    
    if (!user) {
      alert('Vous devez être connecté pour contacter le vendeur');
      return;
    }

    if (listing.user_id === user.id) {
      alert('Vous ne pouvez pas vous contacter vous-même');
      return;
    }
    
    setIsContacting(true);
    try {
      const result = await apiService.contactSeller(listing.id);
      if (result.success) {
        onContact && onContact(result.data);
      } else {
        alert('Erreur lors de la création de la conversation: ' + result.message);
      }
    } catch (error) {
      console.error('Erreur lors du contact:', error);
      alert('Erreur lors de la création de la conversation');
    } finally {
      setIsContacting(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Découvrez cette annonce: ${listing.title}`,
        url: window.location.href
      });
    } else {
      // Fallback pour copier dans le presse-papier
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papier');
    }
  };

  const getImageUrl = (image) => {
    if (typeof image === 'string') {
      return image;
    } else if (image && typeof image === 'object') {
      return image.secure_url || image.url || image.original || '';
    }
    return 'https://via.placeholder.com/400x300?text=No+Image';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getConditionText = (condition) => {
    const conditions = {
      new: 'Neuf',
      like_new: 'Comme neuf',
      good: 'Bon état',
      fair: 'État correct',
      poor: 'Mauvais état'
    };
    return conditions[condition] || condition;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Annonce introuvable</h2>
          <p className="text-gray-600 mb-4">Cette annonce n'existe pas ou a été supprimée.</p>
          <button 
            onClick={onBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between p-4">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon />
          </button>
          <h1 className="font-semibold text-gray-900 truncate flex-1 mx-4">
            {listing.title}
          </h1>
          <button 
            onClick={handleShare}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ShareIcon />
          </button>
        </div>
      </div>

      {/* Images */}
      {images.length > 0 && (
        <div className="relative">
          <div className="aspect-w-16 aspect-h-9">
            <img
              src={getImageUrl(images[currentImageIndex])}
              alt={`${listing.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-64 object-cover"
            />
          </div>
          
          {images.length > 1 && (
            <>
              {/* Navigation des images */}
              <div className="absolute inset-y-0 left-0 flex items-center">
                <button
                  onClick={() => setCurrentImageIndex(prev => 
                    prev === 0 ? images.length - 1 : prev - 1
                  )}
                  className="ml-2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              
              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  onClick={() => setCurrentImageIndex(prev => 
                    prev === images.length - 1 ? 0 : prev + 1
                  )}
                  className="mr-2 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Indicateurs */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                    }`}
                  />
                ))}
              </div>

              {/* Compteur d'images */}
              <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}

      {/* Contenu principal */}
      <div className="p-4">
        {/* Prix et titre */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h1>
          <p className="text-3xl font-bold text-blue-600">{formatPrice(listing.price)}</p>
        </div>

        {/* Informations principales */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">État</p>
              <p className="font-medium">{getConditionText(listing.condition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Publié le</p>
              <p className="font-medium">{formatDate(listing.created_at)}</p>
            </div>
            {listing.location && (
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Localisation</p>
                <div className="flex items-center gap-1">
                  <LocationIcon />
                  <p className="font-medium">{listing.location}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {listing.description && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{listing.description}</p>
          </div>
        )}

        {/* Statistiques */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-around text-center">
            <div>
              <p className="text-lg font-semibold text-gray-900">{listing.views_count || 0}</p>
              <p className="text-sm text-gray-600">Vues</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">{listing.favorites_count || 0}</p>
              <p className="text-sm text-gray-600">Favoris</p>
            </div>
          </div>
        </div>

        {/* Vendeur */}
        {listing.seller && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Vendeur</h2>
            <div className="flex items-center gap-3">
              <AvatarWithFallback
                src={listing.seller.avatar_url}
                alt={`${listing.seller.first_name} ${listing.seller.last_name}`}
                size="w-12 h-12"
              />
              <div>
                <p className="font-medium text-gray-900">
                  {listing.seller.first_name} {listing.seller.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  Membre depuis {new Date(listing.seller.created_at).getFullYear()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions fixes en bas */}
      {user && listing.user_id !== user.id && (
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <div className="flex gap-3">
            <button
              onClick={handleLike}
              disabled={isProcessing}
              className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                isLiked 
                  ? 'bg-red-50 border-red-200 text-red-600' 
                  : 'bg-gray-50 border-gray-200 text-gray-600'
              } hover:bg-opacity-80 disabled:opacity-50`}
            >
              <HeartIcon filled={isLiked} />
            </button>
            
            <button
              onClick={handleContact}
              disabled={isContacting}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isContacting ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Contact...
                </>
              ) : (
                <>
                  <MessageIcon />
                  Contacter le vendeur
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
